import React from 'react';

import { Flex, Label, Text, RadioGroup, LssProp } from '@lucidhq/lucidium';
import { useFormikContext } from '@lucidhq/lucidium/components/FormV2';

import { FieldErrorMessage } from '~/components/Shared/FieldErrorMessage';
import { CampaignFormValues } from '~/impact-measurement/pages/types';
import { t } from '~/utils/i18n';

const radioGroupContainerLss: LssProp = {
  flexDirection: 'row',
  gap: '2.5rem',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  pt: 'xs',
  height: 'fit-content'
};

type ExpectedMediaChannelsProps = {
  isDigitalChecked: boolean;
  isReadOnly: boolean;
};

export enum CTV_VALUES {
  CTV = 'ctv',
  NONE = 'none'
}

const CTV_LABELS = {
  YES: t('forms.labels.yes'),
  NO: t('forms.labels.no')
};

const CTV_OPTIONS = [
  { label: CTV_LABELS.YES, value: CTV_VALUES.CTV },
  { label: CTV_LABELS.NO, value: CTV_VALUES.NONE }
];

const EXPECTED_MEDIA_CHANNELS = 'draft_settings.expected_media_channels';

export const getExpectedMediaChannelsValue = (values: CampaignFormValues) => {
  if (
    !!values?.draft_settings?.expected_media_channels?.includes(CTV_VALUES.CTV)
  ) {
    return CTV_VALUES.CTV;
  }

  if (
    Array.isArray(values?.draft_settings?.expected_media_channels) &&
    !values?.draft_settings?.expected_media_channels?.length
  ) {
    return CTV_VALUES.NONE;
  }

  return null;
};

export const ExpectedMediaChannelsSection = ({
  isDigitalChecked,
  isReadOnly
}: ExpectedMediaChannelsProps) => {
  const { setFieldValue, values, errors, dirty } = useFormikContext<
    CampaignFormValues
  >();

  const expectedMediaChannelsValue = getExpectedMediaChannelsValue(values);

  if (!isDigitalChecked) return null;

  return (
    <Flex
      id="expected-media-channels-field"
      lss={{
        flexDirection: 'column',
        mb: isDigitalChecked ? 'md' : 'none'
      }}
    >
      <Label lss={{ my: 'sm' }} required>
        {t('forms.labels.expectedMediaChannels')}
      </Label>
      <Flex lss={radioGroupContainerLss}>
        <Text lss={{ textScale: 'control' }}>{t('forms.labels.ctv')}</Text>
        <RadioGroup
          isInline
          disabled={isReadOnly}
          name={EXPECTED_MEDIA_CHANNELS}
          options={CTV_OPTIONS}
          value={expectedMediaChannelsValue}
          optionLss={{ mb: 'none' }}
          onChange={value => {
            const expectedMediaChannelsVal =
              value === CTV_VALUES.CTV ? [CTV_VALUES.CTV] : [];

            setFieldValue(EXPECTED_MEDIA_CHANNELS, expectedMediaChannelsVal);
          }}
        />
      </Flex>
      {dirty && !!errors?.draft_settings?.expected_media_channels && (
        <FieldErrorMessage
          message={t('forms.errors.requiredField')}
          lss={{ mt: 'none', pt: 'xxs' }}
        />
      )}
    </Flex>
  );
};
